"use client";
import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Stack } from "@/components/ui/layout";
import { Text } from "@/components/ui/typography";
import { Play, Circle, Square, RotateCcw, Save, X, CheckCircle2, AlertCircle, Loader2, Video, VideoOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { getUploadConfig, uploadVideoDirect, uploadVideoToServer } from "./videoUpload";

function VideoPreviewArea({
  videoRef, isCameraOn, isRecording, recordedVideoURL,
  startCamera, stopCamera, startRecording, stopRecording, startOver,
  onVideoSaved, onSaveStart
}) {
  const previewRef = useRef(null);
  const [recordTime, setRecordTime] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    if (recordedVideoURL && previewRef.current) {
      previewRef.current.srcObject = null;
      previewRef.current.src = recordedVideoURL;
      previewRef.current.onloadedmetadata = () => {
        previewRef.current.play().catch((error) => {
          console.warn("Autoplay blocked:", error);
        });
      };
      previewRef.current.load();
    }
  }, [recordedVideoURL]);

  // Ensure video element receives and plays the camera stream
  useEffect(() => {
    if (isCameraOn && videoRef.current && !recordedVideoURL) {
      const video = videoRef.current;
      
      // Set up event handlers
      const handleLoadedMetadata = () => {
        video.play().catch((error) => {
          console.warn("Video autoplay blocked:", error);
        });
      };

      const handlePlay = () => {
        console.log("Video is playing");
      };

      const handleError = (error) => {
        console.error("Video element error:", error);
      };

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('play', handlePlay);
      video.addEventListener('error', handleError);

      // Check if srcObject is set, if not, wait a bit and check again
      // This handles the case where the parent component sets it asynchronously
      const checkStream = setInterval(() => {
        if (video.srcObject) {
          clearInterval(checkStream);
          video.play().catch((error) => {
            console.warn("Video play failed:", error);
          });
        }
      }, 100);

      // If srcObject is already set, try to play immediately
      if (video.srcObject) {
        clearInterval(checkStream);
        video.play().catch((error) => {
          console.warn("Video play failed:", error);
        });
      }

      // Cleanup after 5 seconds max
      const timeout = setTimeout(() => {
        clearInterval(checkStream);
      }, 5000);

      // Cleanup
      return () => {
        clearInterval(checkStream);
        clearTimeout(timeout);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, [isCameraOn, recordedVideoURL]);

  useEffect(() => {
    if (isRecording) {
      setRecordTime(0);
      timerRef.current = setInterval(() => {
        setRecordTime(prevTime => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleSaveClick = () => {
    if (recordTime < 10) {
      toast.error("Video too short", {
        description: "Video must be at least 10 seconds long to save.",
      });
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirmSave = async () => {
    setIsModalOpen(false);
    setUploadStatus('uploading');

    // Let the parent snapshot the current review timestamp BEFORE we upload,
    // so its review panel can detect the freshly-generated review afterwards.
    if (onSaveStart) { try { await onSaveStart(); } catch { /* noop */ } }

    try {
      // Convert blob to file
      const response = await fetch(recordedVideoURL);
      const blob = await response.blob();
      const file = new File([blob], "recorded-video.webm", { type: "video/webm" });

      // Direct-to-S3 when enabled (no app-server size limit), else legacy upload.
      const cfg = await getUploadConfig();
      if (cfg.directUpload) {
        await uploadVideoDirect({
          endpoints: { presign: '/api/video-resume/presign', finalize: '/api/video-resume/finalize' },
          file, durationSeconds: recordTime,
        });
      } else {
        await uploadVideoToServer({ url: '/api/upload-video', file, durationSeconds: recordTime });
      }
      const uploadResponse = { ok: true };
  
      const result = {};
      
      if (uploadResponse.ok) {
        setUploadStatus('success');
        
        // Call the onVideoSaved callback if provided
        if (onVideoSaved) {
          onVideoSaved();
        }
  
        toast.success("Video uploaded successfully!", {
          description: "Your video has been saved and is ready for review.",
          duration: 5000,
        });
  
      } else {
        setUploadStatus(`error: ${result.error || 'Something went wrong'}`);
        toast.error("Upload failed", {
          description: result.error || 'Something went wrong. Please try again.',
          duration: 5000,
        });
      }
    } catch (error) {
      setUploadStatus(`error: ${error.message || 'Upload failed'}`);
      console.error('Upload error:', error);
      toast.error("Upload failed", {
        description: error.message || 'Upload failed. Please try again.',
        duration: 5000,
      });
    }
  };

  const handleStartRecording = () => {
    setCountdown(3);
    let counter = 3;
    const countdownInterval = setInterval(() => {
      counter -= 1;
      if (counter === 0) {
        clearInterval(countdownInterval);
        setCountdown(0);
        startRecording();
      } else {
        setCountdown(counter);
      }
    }, 1000);
  };

  const getUploadStatusAlert = () => {
    if (!uploadStatus) return null;
    
    if (uploadStatus === 'uploading') {
      return (
        <Alert className="mt-4 border-primary/50 bg-primary/5">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <AlertDescription className="text-sm text-foreground">
            Uploading your video...
          </AlertDescription>
        </Alert>
      );
    }
    
    if (uploadStatus.startsWith('success')) {
      return (
        <Alert className="mt-4 border-success/50 bg-success/5">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-sm text-foreground">
            Video uploaded successfully!
          </AlertDescription>
        </Alert>
      );
    }
    
    if (uploadStatus.startsWith('error:')) {
      const errorMessage = uploadStatus.replace('error: ', '');
      return (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            {errorMessage}
          </AlertDescription>
        </Alert>
      );
    }
    
    return null;
  };

  return (
    <div className={cn("flex flex-col items-center justify-center w-full relative p-4 overflow-hidden")}>
      {!isCameraOn && !recordedVideoURL ? (
        <Card className={cn("w-full max-w-md border-2 border-dashed border-border bg-card/50 backdrop-blur-sm")}>
          <CardContent className={cn("p-12")}>
            <Stack spacing="lg" align="center">
              <div className={cn(
                "rounded-full bg-primary/10 p-6",
                "text-primary animate-pulse transition-all"
              )}>
                <Play className="w-12 h-12 fill-primary stroke-primary" />
              </div>
              <div className={cn("text-center space-y-2")}>
                <Text variant="h3" className={cn("font-semibold text-foreground")}>
                  Ready to Record
                </Text>
                <Text variant="muted" className={cn("text-sm")}>
                  Click below to start your camera and begin recording
                </Text>
              </div>
              <Button onClick={startCamera} size="lg" className={cn("gap-2 min-w-[200px]")}>
                <Play className="w-5 h-5" />
                Start Camera
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <div className={cn("relative w-full max-w-4xl flex flex-col items-center space-y-6")}>
          <div className={cn("relative w-full aspect-video rounded-xl overflow-hidden", "border-2 border-border bg-black shadow-xl")}>
            {isRecording && (
              <Badge 
                variant="destructive" 
                className={cn(
                  "absolute top-4 left-4 z-10",
                  "px-4 py-2 text-sm font-semibold",
                  "shadow-lg animate-pulse"
                )}
              >
                <span className={cn("w-2 h-2 bg-white rounded-full mr-2 animate-pulse")}></span>
                <span>REC</span>
                <span className={cn("ml-2 font-mono")}>{formatTime(recordTime)}</span>
              </Badge>
            )}

            {countdown > 0 && (
              <div className={cn(
                "absolute inset-0 flex items-center justify-center z-20",
                "bg-background/80 backdrop-blur-sm rounded-xl",
                "border-2 border-primary/20"
              )}>
                <div className={cn(
                  "text-8xl font-bold text-primary",
                  "animate-pulse drop-shadow-lg"
                )}>
                  {countdown}
                </div>
              </div>
            )}
            {recordedVideoURL ? (
              <video 
                ref={previewRef} 
                className={cn("w-full h-full object-contain")} 
                controls 
              />
            ) : (
              <video 
                ref={videoRef} 
                className={cn("w-full h-full object-cover")} 
                autoPlay 
                playsInline 
                muted
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    videoRef.current.play().catch((error) => {
                      console.warn("Video autoplay blocked:", error);
                    });
                  }
                }}
              />
            )}
          </div>

          <div className={cn("flex flex-wrap items-center justify-center gap-3 w-full")}>
            {/* Camera is on, ready to record */}
            {!isRecording && !recordedVideoURL && (
              <>
                <Button variant="outline" onClick={stopCamera} size="lg" className={cn("gap-2")}>
                  <VideoOff className="w-4 h-4" />
                  End Camera
                </Button>
                <Button 
                  onClick={handleStartRecording} 
                  size="lg"
                  className={cn("gap-2")}
                >
                  <Circle className="w-4 h-4 fill-current" />
                  Start Recording
                </Button>
              </>
            )}
            
            {/* Recording in progress */}
            {isRecording && (
              <Button 
                onClick={stopRecording} 
                variant="destructive"
                size="lg"
                className={cn("gap-2")}
              >
                <Square className="w-4 h-4" />
                Stop Recording
              </Button>
            )}
            
            {/* Recording finished, preview available */}
            {recordedVideoURL && (
              <>
                <Button 
                  variant="outline" 
                  onClick={startOver}
                  size="lg"
                  className={cn("gap-2")}
                >
                  <RotateCcw className="w-4 h-4" />
                  Retake Video
                </Button>
                <Button 
                  onClick={handleSaveClick}
                  size="lg"
                  className={cn("gap-2")}
                >
                  <Save className="w-4 h-4" />
                  Save & Upload
                </Button>
              </>
            )}
          </div>
          
          {getUploadStatusAlert()}
        </div>
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onConfirm={handleConfirmSave} 
      />
    </div>
  );
}

export default VideoPreviewArea;